import { useMemo } from 'react';

import { useTypingEffect } from '#ui/hooks/useTypingEffect.js';
import { Logo } from '#ui/logo.js';

type GuideType = 'destination' | 'hotel' | 'flight' | 'place' | 'room';

const haikus: Record<GuideType, string> = {
  destination: 'The map trails off here,\nA city unnamed, unseen,\nNew worlds wait beyond.',
  hotel: 'A door left ajar,\nThe room beyond has gone dark,\nLight lives down the hall.',
  flight: 'Empty gate at dawn,\nThe flight that slipped through your hands,\nSkies hold other wings.',
  place: 'Footprints in the sand,\nWashed away by gentle tides,\nNew shores call your name.',
  room: 'A key with no lock,\nThe hallway stretches and fades,\nOther doors await.',
};

const fallbackHaiku = 'The path fades to mist,\nA traveler without a map,\nNew roads still await.';

interface GuideNotFoundProps {
  title: string;
  guide?: GuideType;
}

export function GuideNotFound({ title, guide }: GuideNotFoundProps) {
  const haiku = guide ? haikus[guide] : fallbackHaiku;

  const { displayedText, isTyping } = useTypingEffect(haiku, true, { speed: 15 });

  const lines = useMemo(() => displayedText.split('\n'), [displayedText]);

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="mx-auto max-w-[320px] text-left min-h-[160px]">
        <Logo variant="symbol" width={24} />
        <h1 className="mt-4 text-2xl font-semibold font-display">{title}</h1>

        <div className="mt-2 text-muted-foreground min-h-[4rem]">
          {lines.map((line, index) => (
            <p key={index} className={isTyping && index === lines.length - 1 ? 'inline' : ''}>
              {line}
              {isTyping && index === lines.length - 1 && <span className="animate-pulse">|</span>}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}
